import { useQuery } from '@apollo/react-hooks';
import type { ProjectId } from '@atlassian/jira-shared-types';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types';
import type { DateRange, IssueTableData, TopFilter } from '../../common/types';
import { getMaybeCombinedError } from '../../common/utils/errors';
import { useGiraClient } from '../../common/utils/graphql-clients';
import { useHistoricalCycleTimeData } from '../historical-cycle-time';
import { usePerIssueMetrics } from '../per-issue-metrics';
import type {
	jira_cycleTimeReport_issuesQueryByJql_Query as IssueQuery,
	jira_cycleTimeReport_issuesQueryByJql_QueryVariables as IssueQueryVariables,
} from './__generated_apollo__/jira_cycleTimeReport_issuesQueryByJql_Query';
import { ISSUES_QUERY_BY_JQL } from './gql';
import type { FetchMoreFn } from './types';
import {
	buildVariables,
	getIssueIdsFromIssueMetrics,
	transformResult,
	useAutoPagination,
} from './utils';

export const useIssueTableData = (
	projectId: ProjectId | null,
	selectedDateRange: DateRange | null,
	filters: TopFilter,
): ServiceQueryResultWithFetch<IssueTableData> => {
	const giraClient = useGiraClient();
	const perIssueMetricsResult = usePerIssueMetrics(projectId, selectedDateRange, filters);
	const historicalCycleTimeResult = useHistoricalCycleTimeData(projectId, filters);

	const { data: perIssueMetricsData } = perIssueMetricsResult;
	const { data: historicalCycleTimeData } = historicalCycleTimeResult;

	const combinedError = getMaybeCombinedError([
		perIssueMetricsResult.error,
		historicalCycleTimeResult.error,
	]);

	const skip =
		projectId === null ||
		selectedDateRange === null ||
		perIssueMetricsData == null ||
		perIssueMetricsData.length === 0 ||
		historicalCycleTimeData == null;

	const queryResult = useQuery<IssueQuery, IssueQueryVariables>(ISSUES_QUERY_BY_JQL, {
		variables: buildVariables(getIssueIdsFromIssueMetrics(perIssueMetricsData), projectId, null),
		client: giraClient,
		skip,
	});

	// @ts-expect-error - TS2322 - Type '(<K extends keyof jira_cycleTimeReport_issuesQueryByJql_QueryVariables>(fetchMoreOptions: FetchMoreQueryOptions<jira_cycleTimeReport_issuesQueryByJql_QueryVariables, K> & FetchMoreOptions<...>) => Promise<...>) & (<TData2, TVariables2, K extends keyof TVariables2>(fetchMoreOptions: { ...; } & ... 1 more ... & FetchM...' is not assignable to type 'FetchMoreFn'.
	const fetchMoreFn: FetchMoreFn = queryResult.fetchMore;
	const after =
		queryResult.data?.issues?.pageInfo?.hasNextPage === true
			? queryResult.data?.issues?.pageInfo?.endCursor
			: null;
	useAutoPagination(fetchMoreFn, after, perIssueMetricsData, projectId, queryResult.loading);

	if (combinedError) {
		return { loading: false, error: combinedError, fetch: () => Promise.resolve() };
	}

	if (
		projectId === null ||
		selectedDateRange === null ||
		perIssueMetricsData == null ||
		historicalCycleTimeData == null
	) {
		return { loading: true, fetch: () => Promise.resolve() };
	}

	if (perIssueMetricsData.length === 0) {
		return {
			fetch: () => Promise.resolve(),
			loading: false,
			data: {
				rows: [],
				cycleTimeStatistic: 0,
			},
		};
	}

	// @ts-expect-error - Type 'ServiceQueryResultWithFetch<IssueTableData, (variables?: jira_cycleTimeReport_issuesQueryByJql_QueryVariables | undefined) => Promise<...>>' is not assignable to type 'ServiceQueryResultWithFetch<IssueTableData, () => Promise<void>>'.
	return transformResult(
		queryResult,
		perIssueMetricsData,
		historicalCycleTimeData.hasEnoughDataForMeaningfulStatistics
			? historicalCycleTimeData.wholePeriodStatistics
			: null,
	);
};
